import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import Empty from '../../components/empty/empty';
import ErrorComponent from '../../components/error/error';
import Infos from '../../components/infos/infos';
import Loading from '../../components/loading/loading';

// Models
import { BaseContentContainerFormat } from '../../models/base-content-container';
import { DiaryType } from '../../models/diary-models';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import DiaryService from '../../services/diary-service';
import AbsenceService from '../../services/absence-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

// Helpers
import { getAbsenceReasonLabel } from '../../helpers/absence-helper';

const DiaryFollow = () => {
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const navigate = useNavigate();

  const { classroomUuid, studentUuid } = useParams<{ classroomUuid: string; studentUuid: string }>();
  const [error, setError] = useState(false);
  const [schoolUuid, setSchoolUuid] = useState<string>(null);

  const [activeEntrie, setActiveEntrie] = useState(0);
  const [entries, setEntries] = useState<StudentRecord[]>(null);
  useEffect(() => {
    new SchoolService()
      .getSchoolById(new StorageService().getEmployee().schoolId)
      .then((school) => {
        setSchoolUuid(school.uuid);

        return new DiaryService(school.uuid).listStudentRecords(studentUuid);
      })
      .then((newEntries) => setEntries(newEntries))
      .catch(() => setError(true));
  }, [studentUuid]);

  if (error) return <ErrorComponent type='contentFull' />;

  if (!entries) return <Loading type='contentFull' />;

  if (entries.length === 0) return <Empty />;

  const deleteDiaryCallback = (entryId: string) => {
    setModalContent({
      title: 'Deletar entrada',
      modalContent: <p>Deseja realmente deletar esta entrada?</p>,
      submitButtonTitle: 'confirmar deleção',
      submitButtonType: 'primary',
      submitData: null,
      onSubmitButtonClick: () =>
        new DiaryService(schoolUuid)
          .deleteDiaryEntry(entryId)
          .then(() => {
            setToastContent({
              isOpen: true,
              text: 'Entrada deletada!',
              type: 'success',
            });
            navigate(`/diarios/turma/${classroomUuid}/historico`);
          })
          .catch((err) => {
            setToastContent({
              isOpen: true,
              text: err?.status === 409
                ? 'O diário mudou enquanto você olhava. Recarregue a página e tente de novo.'
                : 'Houve um erro ao deletar a entrada.',
              type: 'error',
            });
          }),
      isOpen: true,
    });
  };

  const deleteAbsenceCallback = (absenceId: string) => {
    setModalContent({
      title: 'Deletar falta',
      modalContent: <p>Deseja realmente deletar esta falta?</p>,
      submitButtonTitle: 'confirmar deleção',
      submitButtonType: 'primary',
      submitData: null,
      onSubmitButtonClick: () =>
        new AbsenceService(schoolUuid)
          .deleteAbsences([absenceId])
          .then((res) => {
            // Professor só apaga a falta que registrou; a API pula as outras sem erro.
            if (res.deletedCount === 0) {
              setToastContent({
                isOpen: true,
                text: 'Só quem registrou a falta ou um administrador pode deletá-la.',
                type: 'warning',
              });
              return;
            }

            setToastContent({
              isOpen: true,
              text: 'Falta deletada!',
              type: 'success',
            });
            navigate(`/diarios/turma/${classroomUuid}/historico`);
          })
          .catch(() => {
            setToastContent({
              isOpen: true,
              text: 'Houve um erro ao deletar a falta.',
              type: 'error',
            });
          }),
      isOpen: true,
    });
  };

  let diaryData: InfosDataDiarySimple | InfosDataDiaryDetailed | InfosDataAbsence;
  const currentEntry = entries[activeEntrie];
  const entrieDate = parseISO(currentEntry.date);

  if (currentEntry.kind === 'Absence') {
    const { absence } = currentEntry;
    diaryData = {
      type: 'absence',
      entrieDate,
      reporterName: absence.createdByName || '-',
      absenceData: {
        absenceDate: parseISO(absence.date),
        isJustified: Boolean(absence.justification),
        reason: absence.justification?.reason ?? null,
        details: absence.justification?.details,
        sickNote: absence.sickNote,
        isDeletable: currentEntry.isDeletable,
        deleteAbsenceCallback: () => deleteAbsenceCallback(absence.id),
      },
    };
  } else if (currentEntry.diary.mode === DiaryType.simple) {
    const { diary } = currentEntry;
    diaryData = {
      type: 'diary',
      diaryType: DiaryType.simple,
      entrieDate,
      reporterName: diary.authorName || '-',
      diaryData: {
        details: diary.content.details,
        isDeletable: currentEntry.isDeletable,
        deleteDiaryCallback: () => deleteDiaryCallback(diary.entryId),
      },
    };
  } else if (currentEntry.diary.mode === DiaryType.detailed) {
    const { diary } = currentEntry;
    diaryData = {
      type: 'diary',
      diaryType: DiaryType.detailed,
      entrieDate,
      reporterName: diary.authorName || '-',
      diaryData: {
        sleep: diary.content.sleep,
        evacuate: diary.content.evacuate,
        shower: diary.content.shower,
        diaper: diary.content.diaper,
        breakfast: diary.content.breakfast,
        afternoonCoffee: diary.content.afternoonCoffee,
        lunch: diary.content.lunch,
        dinner: diary.content.dinner,
        details: diary.content.details,
        isDeletable: currentEntry.isDeletable,
        deleteDiaryCallback: () => deleteDiaryCallback(diary.entryId),
      },
    };
  }

  const engagementData: InfosDataList = {
    type: 'list',
    content: {
      titles: [
        {
          text: 'Tipo',
        },
        {
          text: 'Visualizado por',
        },
        {
          text: 'Data da entrada',
          customStyle: { textAlign: 'right' },
        },
      ],
      rows: entries.map((entrie, index) => {
        let typeText = 'Diário';
        if (entrie.kind === 'Absence') {
          const reasonLabel = entrie.absence.justification
            ? getAbsenceReasonLabel(entrie.absence.justification.reason)
            : 'Não justificada';
          typeText = `Falta (${reasonLabel})`;
        }

        const seen = entrie.kind === 'Absence' ? entrie.absence.seen : entrie.diary.seen;

        return [
          {
            text: typeText,
            onClick: () => setActiveEntrie(index),
          },
          {
            text: seen?.guardianName || '-',
            onClick: () => setActiveEntrie(index),
          },
          {
            text: format(parseISO(entrie.date), 'dd/MM/yyyy'),
            customStyle: { textAlign: 'right' },
            onClick: () => setActiveEntrie(index),
          },
        ];
      }),
    },
  };

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithoutActionButtons}
      withoutShadowBox={true}
    >
      <Infos title='entrada' titleColor='intermediate' data={diaryData} />
      <Infos
        title='entradas anteriores'
        titleColor='curiosity'
        backgroundColor='gray'
        data={engagementData}
      />
    </BaseContentContainer>
  );
};

export default DiaryFollow;
